import { useEffect, useRef, useMemo, useState, useCallback } from 'react'
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  Time,
  ColorType,
  CrosshairMode,
} from 'lightweight-charts'
import { createLineToolsPlugin, ILineToolsPlugin } from 'lightweight-charts-line-tools-core'
import { registerLinesPlugin } from 'lightweight-charts-line-tools-lines'
import { LineToolRectangle } from 'lightweight-charts-line-tools-rectangle'
import { registerFibRetracementPlugin } from 'lightweight-charts-line-tools-fib-retracement'
import { registerPriceRangePlugin } from 'lightweight-charts-line-tools-price-range'
import type { Candle, Signal } from '@/types'
import { SignalMarkersPrimitive, SignalMarkerData } from './SignalMarkersPrimitive'
import { DrawingToolbar } from './DrawingToolbar'

interface TradingViewChartProps {
  candles: Candle[]
  signals: Signal[]
  symbol: string
  timeframe: string
  realtimeCandle?: Candle | null
  onVisibleRangeChange?: (from: number, to: number) => void
}

// Timeframe to seconds mapping
const TIMEFRAME_SECONDS: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
}

// Base thresholds for size ratio calculation
const BASE_THRESHOLDS: Record<string, number> = {
  dom: 1000,
  trade: 100,
}
const MAX_SIZE_RATIO = 4.0

/**
 * Calculate size ratio: how many times the signal exceeds the base threshold.
 * Capped at MAX_SIZE_RATIO. Minimum 1.0.
 * E.g. DOM 2000 BTC → 2.0x, Trade 350 BTC → 3.5x
 */
function getSizeRatio(quantity: number, source: string): number {
  const base = BASE_THRESHOLDS[source] ?? 1000
  return Math.min(Math.max(quantity / base, 1.0), MAX_SIZE_RATIO)
}

function groupSignals(signals: Signal[], timeframeSeconds: number): SignalMarkerData[] {
  const groups = new Map<string, SignalMarkerData>()

  for (const signal of signals) {
    const signalTime = Math.floor(new Date(signal.detected_at).getTime() / 1000)
    // Round to candle time
    const candleTime = Math.floor(signalTime / timeframeSeconds) * timeframeSeconds
    const price = Math.round(signal.price) // Round price to integer for grouping
    const side = signal.side === 'ask' || signal.side === 'sell' ? 'ask' : 'bid'
    const source = signal.source === 'trade' ? 'trade' : 'dom'
    const domMode = source === 'dom' ? (signal.dom_mode ?? null) : null
    const key = `${candleTime}-${price}-${side}-${source}-${domMode ?? ''}`

    const existing = groups.get(key)
    if (existing) {
      existing.count++
      if (signal.quantity > existing.maxQuantity) {
        existing.maxQuantity = signal.quantity
        existing.sizeRatio = getSizeRatio(signal.quantity, source)
      }
    } else {
      groups.set(key, {
        time: candleTime,
        price,
        side,
        source,
        domMode,
        maxQuantity: signal.quantity,
        count: 1,
        sizeRatio: getSizeRatio(signal.quantity, source),
      })
    }
  }

  return Array.from(groups.values())
}

function getDrawingsKey(symbol: string): string {
  return `drawings-${symbol}`
}

export function TradingViewChart({
  candles,
  signals,
  symbol,
  timeframe,
  realtimeCandle,
  onVisibleRangeChange,
}: TradingViewChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  const signalPrimitiveRef = useRef<SignalMarkersPrimitive | null>(null)
  const lineToolsRef = useRef<ILineToolsPlugin | null>(null)
  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange)
  const lastCandleCountRef = useRef(0)
  const symbolRef = useRef(symbol)

  const [activeTool, setActiveTool] = useState<string | null>(null)

  // Group signals
  const groupedSignals = useMemo(() => {
    const tfSeconds = TIMEFRAME_SECONDS[timeframe] || 300
    return groupSignals(signals, tfSeconds)
  }, [signals, timeframe])

  // Keep callback ref updated
  useEffect(() => {
    onVisibleRangeChangeRef.current = onVisibleRangeChange
  }, [onVisibleRangeChange])

  // Keep symbol ref updated
  useEffect(() => {
    symbolRef.current = symbol
  }, [symbol])

  // Save drawings to localStorage
  const saveDrawings = useCallback(() => {
    if (!lineToolsRef.current) return
    try {
      const data = lineToolsRef.current.exportLineTools()
      localStorage.setItem(getDrawingsKey(symbolRef.current), data)
    } catch {
      // Ignore save errors
    }
  }, [])

  // Initialize chart - only once
  useEffect(() => {
    if (!chartContainerRef.current) return

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#131722' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#1e222d' },
        horzLines: { color: '#1e222d' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: '#758696',
          width: 1,
          style: 2,
          labelBackgroundColor: '#2a2e39',
        },
        horzLine: {
          color: '#758696',
          width: 1,
          style: 2,
          labelBackgroundColor: '#2a2e39',
        },
      },
      rightPriceScale: {
        borderColor: '#2a2e39',
        scaleMargins: {
          top: 0.1,
          bottom: 0.2,
        },
      },
      timeScale: {
        borderColor: '#2a2e39',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    })

    // Create candlestick series
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    })

    // Create and attach signal markers primitive
    const signalPrimitive = new SignalMarkersPrimitive()
    candleSeries.attachPrimitive(signalPrimitive)
    signalPrimitiveRef.current = signalPrimitive

    // Create volume series
    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
    })

    volumeSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    })

    // Initialize line tools plugin
    const lineTools = createLineToolsPlugin(chart, candleSeries)

    // Register all line tools
    // Cast needed: companion packages expect wider `string` type param,
    // core plugin uses strict `keyof LineToolOptionsMap`
    registerLinesPlugin(lineTools as Parameters<typeof registerLinesPlugin>[0])
    lineTools.registerLineTool('Rectangle', LineToolRectangle)
    registerFibRetracementPlugin(lineTools as Parameters<typeof registerFibRetracementPlugin>[0])
    registerPriceRangePlugin(lineTools as Parameters<typeof registerPriceRangePlugin>[0])

    // Load saved drawings
    const saved = localStorage.getItem(getDrawingsKey(symbolRef.current))
    if (saved) {
      try {
        lineTools.importLineTools(saved)
      } catch {
        // Ignore corrupt data
      }
    }

    // Auto-save on edit
    const afterEditHandler = () => {
      try {
        const data = lineTools.exportLineTools()
        localStorage.setItem(getDrawingsKey(symbolRef.current), data)
      } catch {
        // Ignore save errors
      }
    }
    lineTools.subscribeLineToolsAfterEdit(afterEditHandler)

    lineToolsRef.current = lineTools
    chartRef.current = chart
    candleSeriesRef.current = candleSeries
    volumeSeriesRef.current = volumeSeries

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        })
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    // Subscribe to visible range changes using ref
    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (range && onVisibleRangeChangeRef.current) {
        const from = Math.floor(range.from)
        const to = Math.ceil(range.to)
        onVisibleRangeChangeRef.current(from, to)
      }
    })

    return () => {
      window.removeEventListener('resize', handleResize)
      lineTools.unsubscribeLineToolsAfterEdit(afterEditHandler)
      if (signalPrimitiveRef.current && candleSeriesRef.current) {
        candleSeriesRef.current.detachPrimitive(signalPrimitiveRef.current)
      }
      chart.remove()
      chartRef.current = null
      candleSeriesRef.current = null
      volumeSeriesRef.current = null
      signalPrimitiveRef.current = null
      lineToolsRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-load drawings when symbol changes
  useEffect(() => {
    if (!lineToolsRef.current) return
    lineToolsRef.current.removeAllLineTools()
    const saved = localStorage.getItem(getDrawingsKey(symbol))
    if (saved) {
      try {
        lineToolsRef.current.importLineTools(saved)
      } catch {
        // Ignore corrupt data
      }
    }
  }, [symbol])

  // Update candle data
  useEffect(() => {
    if (!candleSeriesRef.current || !volumeSeriesRef.current || candles.length === 0) return

    const candleData: CandlestickData<Time>[] = candles.map((c) => ({
      time: c.time as Time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }))

    const volumeData = candles.map((c) => ({
      time: c.time as Time,
      value: c.volume,
      color: c.close >= c.open ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
    }))

    // Preserve scroll position when adding older candles
    const timeScale = chartRef.current?.timeScale()
    const visibleRange = timeScale?.getVisibleLogicalRange()

    candleSeriesRef.current.setData(candleData)
    volumeSeriesRef.current.setData(volumeData)

    // If we added candles to the beginning (older data), adjust view
    if (visibleRange && lastCandleCountRef.current > 0) {
      const addedCount = candles.length - lastCandleCountRef.current
      if (addedCount > 0) {
        timeScale?.setVisibleLogicalRange({
          from: visibleRange.from + addedCount,
          to: visibleRange.to + addedCount,
        })
      }
    }

    lastCandleCountRef.current = candles.length
  }, [candles])

  // Real-time candle update via series.update()
  useEffect(() => {
    if (!realtimeCandle || !candleSeriesRef.current || !volumeSeriesRef.current) return
    // Skip update if chart has no data yet (e.g. during timeframe switch)
    if (candles.length === 0) return

    try {
      candleSeriesRef.current.update({
        time: realtimeCandle.time as Time,
        open: realtimeCandle.open,
        high: realtimeCandle.high,
        low: realtimeCandle.low,
        close: realtimeCandle.close,
      })

      volumeSeriesRef.current.update({
        time: realtimeCandle.time as Time,
        value: realtimeCandle.volume,
        color: realtimeCandle.close >= realtimeCandle.open
          ? 'rgba(38, 166, 154, 0.5)'
          : 'rgba(239, 83, 80, 0.5)',
      })
    } catch {
      // Ignore "Cannot update oldest data" errors during timeframe transitions
    }
  }, [realtimeCandle, candles.length])

  // Update signal markers using the custom primitive
  useEffect(() => {
    if (!signalPrimitiveRef.current) return
    signalPrimitiveRef.current.setSignals(groupedSignals)
  }, [groupedSignals])

  // Drawing tool handlers
  const handleSelectTool = useCallback((toolName: string) => {
    if (!lineToolsRef.current) return

    if (activeTool === toolName) {
      // Deselect if clicking the same tool
      setActiveTool(null)
      return
    }

    setActiveTool(toolName)
    // Start interactive drawing — empty points triggers click-to-place mode
    lineToolsRef.current.addLineTool(toolName as Parameters<ILineToolsPlugin['addLineTool']>[0])
  }, [activeTool])

  const handleDeleteSelected = useCallback(() => {
    if (!lineToolsRef.current) return
    lineToolsRef.current.removeSelectedLineTools()
    saveDrawings()
  }, [saveDrawings])

  const handleClearAll = useCallback(() => {
    if (!lineToolsRef.current) return
    lineToolsRef.current.removeAllLineTools()
    localStorage.removeItem(getDrawingsKey(symbolRef.current))
  }, [])

  return (
    <div className="relative w-full h-full">
      <div
        ref={chartContainerRef}
        className="w-full h-full"
      />
      <div className="absolute top-2 left-2 bg-chart-bg/80 px-3 py-1 rounded text-sm text-text-primary">
        {symbol}
      </div>
      <DrawingToolbar
        activeTool={activeTool}
        onSelectTool={handleSelectTool}
        onDeleteSelected={handleDeleteSelected}
        onClearAll={handleClearAll}
      />
    </div>
  )
}

export default TradingViewChart
